import * as React from "react";

import {
  eventLabel,
  variableRoots,
  type PlatformEventType,
  type SampleEvent,
} from "@/shared/domain/triggers";
import { useEventSamples } from "@/shared/domain/triggers/hooks";
import { X as CloseIcon } from "@/shared/foundation/icons";
import { Dialog } from "@/shared/foundation/primitives";
import { RobotoButton } from "@/shared/foundation/primitives/RobotoButton";

import styles from "./SampleEventDialog.module.css";

// Keeps react-json-view out of the main bundle; the dialog is opened rarely.
const JsonTreeView = React.lazy(
  () => import("@/shared/components/JsonTreeView/JsonTreeView"),
);

/**
 * Roots every trigger can reference regardless of which platform events it
 * subscribes to: the envelope, and the trigger itself. Neither is in the
 * catalog, so `variableRoots` cannot vouch for them.
 */
const ALWAYS_SHARED_ROOTS: ReadonlySet<string> = new Set([
  "envelope",
  "trigger",
]);

interface SampleEventDialogProps {
  open: boolean;
  onClose: () => void;
  /** The trigger's subscribed events; the dialog shows one at a time. */
  events: readonly PlatformEventType[];
}

/**
 * What an event looks like, dereferenced, with realistic values -- and the
 * ``root.path`` names a condition or ``{{ }}`` template may use on it.
 *
 * The REST docs show a sample object beside each API; this is that, for the
 * thing a trigger author actually writes against. The sample is built on the
 * server by the evaluator's own namespace, so what it shows is what a template
 * will resolve to rather than a hand-written approximation. Each path is a
 * click-to-copy ``{{path}}``.
 *
 * With several events subscribed, a condition or template may only use roots
 * every one of them exposes (the validator rejects the rest at save time), so
 * paths on a root the other events lack are shown but marked, with the events
 * they are limited to.
 */
export function SampleEventDialog({
  open,
  onClose,
  events,
}: SampleEventDialogProps) {
  const samplesQuery = useEventSamples();
  const [selected, setSelected] = React.useState<PlatformEventType | null>(
    null,
  );

  // Follow the subscription: a previously viewed event that has since been
  // deselected must not linger as the dialog's subject.
  const current =
    selected !== null && events.includes(selected) ? selected : events[0];
  const sample: SampleEvent | undefined =
    current !== undefined ? samplesQuery.data?.samples[current] : undefined;
  const sharedRoots = new Set(variableRoots(events));

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <Dialog.Content className={styles.popup}>
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <Dialog.Title className={styles.title}>Sample payload</Dialog.Title>
            <Dialog.Close
              render={
                <RobotoButton
                  className={styles.close}
                  variant="ghost"
                  size="md"
                  icon={<CloseIcon />}
                  aria-label="Close"
                />
              }
            />
          </div>
          <Dialog.Description className={styles.subtitle}>
            A realistic example of what this trigger will see.
          </Dialog.Description>
          {events.length > 1 && (
            <div
              className={styles.eventPicker}
              role="tablist"
              aria-label="Platform event type"
            >
              {events.map((eventType) => (
                <button
                  key={eventType}
                  type="button"
                  role="tab"
                  aria-selected={eventType === current}
                  className={
                    eventType === current
                      ? styles.eventTabActive
                      : styles.eventTab
                  }
                  onClick={() => setSelected(eventType)}
                >
                  {eventLabel(eventType)}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className={styles.content}>
          {samplesQuery.isError && (
            <p className={styles.error}>
              Could not load the sample: {samplesQuery.error.message}
            </p>
          )}
          {samplesQuery.isPending && (
            <p className={styles.muted}>Loading sample&hellip;</p>
          )}
          {sample !== undefined && (
            <div className={styles.columns}>
              <PathList
                sample={sample}
                sharedRoots={sharedRoots}
                events={events}
              />
              <div className={styles.tree} data-cy="sample-event-json">
                <React.Suspense
                  fallback={<p className={styles.muted}>Rendering&hellip;</p>}
                >
                  <JsonTreeView src={sample.namespace} />
                </React.Suspense>
                {/* The envelope is what a webhook would receive; the namespace
                    above is what a template sees. Most readers want the second,
                    so the first stays folded. */}
                <details className={styles.envelope}>
                  <summary>Raw envelope</summary>
                  <pre>{JSON.stringify(sample.event, null, 2)}</pre>
                </details>
              </div>
            </div>
          )}
        </div>
      </Dialog.Content>
    </Dialog>
  );
}

function PathList({
  sample,
  sharedRoots,
  events,
}: {
  sample: SampleEvent;
  sharedRoots: ReadonlySet<string>;
  events: readonly PlatformEventType[];
}) {
  // Group by root so the list reads as the namespace does: a handful of
  // entities, each with its fields, rather than fifty flat rows.
  const byRoot = new Map<string, string[]>();
  for (const path of sample.paths) {
    const root = path.split(".")[0];
    byRoot.set(root, [...(byRoot.get(root) ?? []), path]);
  }
  const anyUnshared = [...byRoot.keys()].some(
    (root) => !ALWAYS_SHARED_ROOTS.has(root) && !sharedRoots.has(root),
  );

  return (
    <div className={styles.paths} data-cy="sample-event-paths">
      {anyUnshared && (
        <p className={styles.note}>
          Greyed paths exist only on some of the selected events. A condition or
          template may only use paths every selected event exposes.
        </p>
      )}
      {[...byRoot.entries()].map(([root, paths]) => {
        const shared = ALWAYS_SHARED_ROOTS.has(root) || sharedRoots.has(root);
        return (
          <section key={root} className={styles.rootGroup}>
            <h3 className={styles.rootTitle}>
              {root}
              {!shared && (
                <span className={styles.onlyOn}>
                  {" "}
                  &middot; only on {onlyOnLabel(root, events)}
                </span>
              )}
            </h3>
            <ul className={styles.pathList}>
              {paths.map((path) => (
                <li key={path}>
                  <CopyPath path={path} muted={!shared} />
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

/** Which of the selected events expose ``root`` -- the ones a greyed path is limited to. */
function onlyOnLabel(
  root: string,
  events: readonly PlatformEventType[],
): string {
  return events
    .filter((eventType) => variableRoots([eventType]).includes(root))
    .map(eventLabel)
    .join(", ");
}

function CopyPath({ path, muted }: { path: string; muted: boolean }) {
  const [copied, setCopied] = React.useState(false);
  const placeholder = `{{${path}}}`;

  return (
    <button
      type="button"
      className={muted ? styles.pathMuted : styles.path}
      title={`Copy ${placeholder}`}
      onClick={() => {
        void navigator.clipboard.writeText(placeholder).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1200);
        });
      }}
    >
      <code>{path}</code>
      <span className={styles.copyHint} aria-live="polite">
        {copied ? "Copied" : ""}
      </span>
    </button>
  );
}
